import { when, IReactionDisposer } from 'mobx';
import isUndefined from 'lodash/isUndefined';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { ComponentFactory } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-factory';
import { IEditorProps } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-props.interface';
import {
    CommonPlayLogicWithTextEditor,
} from '@src/slide/content-viewer/service/canvas-renderer/entry/player/logic/common-play-logic-with-text-editor';
import { is3DModel, isIframeShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import type { IMediaManager } from '@src/slide/application/service/media-manager/media-manager.interface';
import { checkHasAnimation } from '../../../core/canvas-animations/element-animation/anim-utils';
import type { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import type { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { getTargetComponent } from '../../../core/canvas-animations/element-animation/anim-component-relate';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { checkClickElement } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-pointer-cursor';

export class ShapePlayLogic extends CommonPlayLogicWithTextEditor {
    public mediaManager: IMediaManager | undefined;
    public iframePosterListener: IReactionDisposer;
    declare protected factoryOptions: InterfaceFactoryOptions<IShapeModel>;

    public constructor(factoryOptions: InterfaceFactoryOptions<IShapeModel>, component: ComponentFactory) {
        super(factoryOptions, component);
        this.mediaManager = factoryOptions.mediaManager;
        this.component.inner.onClick(this.handleInnerClick);
        this.component.inner.onTouchStart(this.handleInnerClick);

        if (isIframeShapeModel(factoryOptions.model)) {
            this.addIframePosterListener();
        }
    }

    public init(): void {
        super.init();
        const { model } = this.factoryOptions;
        if (!is3DModel(model) || !this.component.inner.shouldRender3D) {
            // 3d 场景不需要单独渲染 editor
            this.component.inner.renderEditor();
        }
    }

    /**
     * 获取文本的参数以及回调
     */
    public getEditorProps = (): IEditorProps => {
        const { getPlayerReduxStore } = this.factoryOptions;
        return {
            // 后面会补充编辑相关逻辑，比如点击链接
            cursor: this.getCursor(),   // 播放态的Cursor
            openLinkInPlay: (linkData) => {
                this.getAppService().viewableServiceLayer.linkManager.openLinkInPlay(getPlayerReduxStore?.(), linkData);
            },
        };
    };

    public handleInnerClick = () => {
        if (isIframeShapeModel(this.factoryOptions.model) && this.isIframeSwitchAvailable()) {
            this.mediaManager?.addPlayerMediaElementKey(this.factoryOptions.model.key);
        }
    };

    private getCursor() {
        return checkClickElement(this.factoryOptions) ? 'pointer' : 'auto';
    }

    private isIframeSwitchAvailable() {
        const { model } = this.factoryOptions;

        const hasAnimation = checkHasAnimation(
            model.rootGroupModel?.id ?? model.id ?? '',
            this.getSelectionService()?.currentSlide,
        );

        const animWrapper = getTargetComponent(
            model.rootGroupModel?.key ?? model.key,
            this.getAppServices().viewableServiceLayer?.animManager?.playSlideComponent,
        );

        // 当音视频没有动画，或者音视频元素有显示的情况下，可以从图片切换成 dom
        return !hasAnimation || animWrapper.alpha !== 0;
    }

    private addIframePosterListener = () => {
        const { model } = this.factoryOptions;

        this.iframePosterListener = when(
            () => !isUndefined(this.mediaManager?.iframePosterCaches.get(model.key)),
            () => {
                this.component.updateComponent();
            },
        );
    };

    private getAppServices(): ISlideBaseAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            ISlideBaseAppServices;
    }

    private getSelectionService(): ISelectionService | undefined {
        const appService = AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices;
        return appService?.viewableServiceLayer?.selectionService;
    }
}
